export type SpreadsheetGridSelection = {
  col: number
  row: number
  endCol: number
  endRow: number
}

export type SpreadsheetGridKeyCommand = {
  key: string
  shiftKey?: boolean
  ctrlKey?: boolean
  metaKey?: boolean
  altKey?: boolean
}

export type SpreadsheetGridCommandId =
  | 'undo'
  | 'redo'
  | 'start-edit'
  | 'start-edit-with-input'
  | 'start-autosum'
  | 'clear-selection'
  | 'move-selection'
  | 'select-row'
  | 'select-column'
  | 'select-all'
  | 'toggle-bold'
  | 'toggle-italic'
  | 'toggle-underline'
  | 'open-validation-dropdown'
  | 'native-clipboard'
  | 'unhandled'

export type SpreadsheetGridCommandDisposition = 'grid-command' | 'native-event' | 'unhandled'

export type SpreadsheetGridCommandDecision = {
  commandId: SpreadsheetGridCommandId
  disposition: SpreadsheetGridCommandDisposition
  preventDefault: boolean
  stopImmediatePropagation: boolean
  inputText?: string
  nextSelection?: SpreadsheetGridSelection
}

export type SpreadsheetGridCommandContext = {
  hostHandlesUndoRedoShortcuts?: boolean
  isEditing?: boolean
  selection: SpreadsheetGridSelection | null
  rowCount: number
  colCount: number
  visibleRowCount: number
  visibleColCount: number
  hasValidationDropdown?: boolean
  sheetProtection?: {
    enabled?: boolean
    allow_format_cells?: boolean
    allow_resize_rows?: boolean
    allow_resize_columns?: boolean
  } | null
}

const unhandledGridCommandDecision: SpreadsheetGridCommandDecision = {
  commandId: 'unhandled',
  disposition: 'unhandled',
  preventDefault: false,
  stopImmediatePropagation: false,
}

const gridCommandDecision = (
  commandId: SpreadsheetGridCommandId,
  options: Omit<Partial<SpreadsheetGridCommandDecision>, 'commandId' | 'disposition'> = {},
): SpreadsheetGridCommandDecision => ({
  commandId,
  disposition: 'grid-command',
  preventDefault: options.preventDefault ?? true,
  stopImmediatePropagation: options.stopImmediatePropagation ?? true,
  inputText: options.inputText,
  nextSelection: options.nextSelection,
})

const nativeEventDecision = (commandId: SpreadsheetGridCommandId): SpreadsheetGridCommandDecision => ({
  commandId,
  disposition: 'native-event',
  preventDefault: false,
  stopImmediatePropagation: false,
})

const primaryModifierPressed = (command: SpreadsheetGridKeyCommand): boolean => Boolean(command.ctrlKey || command.metaKey)

const normalizedCommandKey = (command: SpreadsheetGridKeyCommand): string => (
  command.key.length === 1 ? command.key.toLowerCase() : command.key
)

const isSpaceCommand = (command: SpreadsheetGridKeyCommand): boolean => (
  command.key === ' ' || command.key === 'Spacebar'
)

const clampIndex = (value: number, count: number): number => (
  Math.min(Math.max(0, Math.floor(value)), Math.max(0, Math.floor(count) - 1))
)

const clampSelection = (
  selection: SpreadsheetGridSelection,
  rowCount: number,
  colCount: number,
): SpreadsheetGridSelection => ({
  col: clampIndex(selection.col, colCount),
  row: clampIndex(selection.row, rowCount),
  endCol: clampIndex(selection.endCol, colCount),
  endRow: clampIndex(selection.endRow, rowCount),
})

const moveSelection = (
  selection: SpreadsheetGridSelection,
  command: SpreadsheetGridKeyCommand,
  context: SpreadsheetGridCommandContext,
): SpreadsheetGridSelection | null => {
  const rowCount = Math.max(1, Math.floor(context.rowCount))
  const colCount = Math.max(1, Math.floor(context.colCount))
  const visibleRows = Math.max(1, Math.floor(context.visibleRowCount))
  const visibleCols = Math.max(1, Math.floor(context.visibleColCount))
  const isShift = Boolean(command.shiftKey)
  const isPrimary = primaryModifierPressed(command)
  const isAlt = Boolean(command.altKey)
  let newCol = isShift ? selection.endCol : selection.col
  let newRow = isShift ? selection.endRow : selection.row

  switch (command.key) {
    case 'ArrowUp':
      newRow = isPrimary ? 0 : Math.max(0, newRow - 1)
      break
    case 'ArrowDown':
      newRow = isPrimary ? rowCount - 1 : Math.min(rowCount - 1, newRow + 1)
      break
    case 'ArrowLeft':
      newCol = isPrimary ? 0 : Math.max(0, newCol - 1)
      break
    case 'ArrowRight':
      newCol = isPrimary ? colCount - 1 : Math.min(colCount - 1, newCol + 1)
      break
    case 'Home':
      if (isPrimary) newRow = 0
      newCol = 0
      break
    case 'End':
      if (isPrimary) newRow = rowCount - 1
      newCol = colCount - 1
      break
    case 'PageUp':
      if (isAlt) newCol = Math.max(0, newCol - visibleCols)
      else newRow = Math.max(0, newRow - visibleRows)
      break
    case 'PageDown':
      if (isAlt) newCol = Math.min(colCount - 1, newCol + visibleCols)
      else newRow = Math.min(rowCount - 1, newRow + visibleRows)
      break
    case 'Enter':
      newRow = isShift ? Math.max(0, selection.row - 1) : Math.min(rowCount - 1, selection.row + 1)
      newCol = selection.col
      return { col: newCol, row: newRow, endCol: newCol, endRow: newRow }
    case 'Tab':
      newCol = isShift ? Math.max(0, selection.col - 1) : Math.min(colCount - 1, selection.col + 1)
      newRow = selection.row
      return { col: newCol, row: newRow, endCol: newCol, endRow: newRow }
    default:
      return null
  }

  if (isShift) {
    return clampSelection({ ...selection, endCol: newCol, endRow: newRow }, rowCount, colCount)
  }
  return { col: newCol, row: newRow, endCol: newCol, endRow: newRow }
}

export const resolveSpreadsheetGridKeyCommand = (
  command: SpreadsheetGridKeyCommand,
  context: SpreadsheetGridCommandContext,
): SpreadsheetGridCommandDecision => {
  const key = normalizedCommandKey(command)
  const isPrimary = primaryModifierPressed(command)
  const selection = context.selection ? clampSelection(context.selection, context.rowCount, context.colCount) : null

  if (context.hostHandlesUndoRedoShortcuts && isPrimary && (key === 'z' || key === 'y')) {
    return unhandledGridCommandDecision
  }
  if (isPrimary && key === 'z') {
    return gridCommandDecision(command.shiftKey ? 'redo' : 'undo')
  }
  if (isPrimary && key === 'y') {
    return gridCommandDecision('redo')
  }

  if (context.isEditing || !selection) return unhandledGridCommandDecision

  if (isPrimary && (key === 'c' || key === 'x' || key === 'v')) {
    return nativeEventDecision('native-clipboard')
  }
  const formatBlockedBySheetProtection = context.sheetProtection?.enabled === true &&
    context.sheetProtection.allow_format_cells !== true
  if (formatBlockedBySheetProtection && isPrimary && (key === 'b' || key === 'i' || key === 'u')) {
    return gridCommandDecision('unhandled')
  }
  if (isPrimary && key === 'b') return gridCommandDecision('toggle-bold')
  if (isPrimary && key === 'i') return gridCommandDecision('toggle-italic')
  if (isPrimary && key === 'u') return gridCommandDecision('toggle-underline')
  if (isSpaceCommand(command) && isPrimary && command.shiftKey) {
    return gridCommandDecision('select-all', {
      nextSelection: { col: 0, row: 0, endCol: Math.max(0, context.colCount - 1), endRow: Math.max(0, context.rowCount - 1) },
    })
  }
  if (isSpaceCommand(command) && isPrimary) {
    return gridCommandDecision('select-column', {
      nextSelection: { col: selection.col, row: 0, endCol: selection.col, endRow: Math.max(0, context.rowCount - 1) },
    })
  }
  if (isSpaceCommand(command) && command.shiftKey) {
    return gridCommandDecision('select-row', {
      nextSelection: { col: 0, row: selection.row, endCol: Math.max(0, context.colCount - 1), endRow: selection.row },
    })
  }
  if (isPrimary && key === 'a') {
    return gridCommandDecision('select-all', {
      nextSelection: { col: 0, row: 0, endCol: Math.max(0, context.colCount - 1), endRow: Math.max(0, context.rowCount - 1) },
    })
  }

  if (command.key === 'F2') return gridCommandDecision('start-edit', { preventDefault: false, stopImmediatePropagation: false })
  if (command.altKey && command.key === '=') return gridCommandDecision('start-autosum')
  if (command.altKey && command.key === 'ArrowDown') {
    return context.hasValidationDropdown
      ? gridCommandDecision('open-validation-dropdown')
      : unhandledGridCommandDecision
  }
  if (command.key === 'Backspace' || command.key === 'Delete') return gridCommandDecision('clear-selection', { stopImmediatePropagation: false })

  const nextSelection = moveSelection(selection, command, context)
  if (nextSelection) return gridCommandDecision('move-selection', { nextSelection })

  if (command.key.length === 1 && !isPrimary && !command.altKey) {
    return gridCommandDecision('start-edit-with-input', {
      inputText: command.key,
      stopImmediatePropagation: false,
    })
  }

  return unhandledGridCommandDecision
}
